import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';

import { Observable, map } from 'rxjs';

import { environment } from '@environments';

class Response<T> {
  data: T | undefined;
  status!: number;
  message: string[] = [];
}

class Request {
  service: string = '';
  method: string = 'get';
  params?: any;
  options?: any;
  context?: any;
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(private http: HttpClient) {}

  private built(service: string): string {
    return (
      environment.endpoint + (service.startsWith('/') ? service : '/' + service)
    );
  }

  private parse<T>(data: any): Response<T> {
    let response = new Response<T>();

    response.status = data.status;
    response.message = data.message;
    response.data = data.data as T | undefined;

    return response;
  }

  external<T = any>(request: Request): Observable<Response<T>> {
    return this.call(request, true);
  }

  call<T = any>(
    request: Request,
    external: boolean = false,
  ): Observable<Response<T>> {
    let request$: Observable<any>;

    const httpOptions = {
      ...request.options,
      context: request.context,
      params:
        request.method.toLowerCase() === 'get' ? request.params : undefined,
    };

    const endpoint = external ? request.service : this.built(request.service);

    switch (request.method.toLowerCase()) {
      case 'post':
        request$ = this.http.post(endpoint, request.params, httpOptions);
        break;
      case 'put':
        request$ = this.http.put(endpoint, request.params, httpOptions);
        break;
      case 'patch':
        request$ = this.http.patch(endpoint, request.params, httpOptions);
        break;
      case 'delete':
        request$ = this.http.delete(endpoint, httpOptions);
        break;
      default:
        request$ = this.http.get(endpoint, httpOptions);
        break;
    }

    return request$.pipe(map((data: any) => this.parse(data)));
  }

  uploadFile<T = any>(
    service: string,
    formData: FormData,
  ): Observable<Response<T>> {
    return this.http
      .post(this.built(service), formData, {
        reportProgress: true,
        observe: 'events',
      })
      .pipe(map((data: any) => this.parse(data)));
  }

  open(service: string, finish: (res: boolean) => void, isFull = false) {
    let url = service;

    if (!isFull) {
      url = this.built(service);
    }

    const options = {
      observe: 'response' as 'body',
      responseType: 'blob' as 'json',
    };

    this.http.get<HttpResponse<any>>(url, options).subscribe({
      next: (response: any) => {
        if (response.body.type == 'application/json') {
          let reader = new FileReader();

          reader.addEventListener('loadend', (e: any) => {
            let json = JSON.parse(e.srcElement.result);
            alert(json.message.join('\n'));
          });

          reader.readAsText(response.body);

          return;
        }

        let hdrs = response.headers.get('Content-Disposition');
        let file = 'document';
        if (hdrs != null) {
          const split = hdrs.split('filename=');

          if (split.length > 0) {
            file = split[1].split('"').join('');
          }
        }

        const u = window.URL.createObjectURL(response.body);
        finish(true);
        window.open(u, '_blank');
      },
      error: (error: any) => {
        console.error(error);
        finish(false);
      },
    });
  }

  downloadSimple(service: string) {
    const options = {
      observe: 'response' as 'body',
      responseType: 'blob' as 'json',
    };

    this.http
      .get<HttpResponse<any>>(this.built(service), options)
      .subscribe((response: any) => {
        if (response.body.type == 'application/json') {
          let reader = new FileReader();

          reader.addEventListener('loadend', (e: any) => {
            let json = JSON.parse(e.srcElement.result);
            alert(json.message.join('\n'));
          });

          reader.readAsText(response.body);

          return;
        }

        let hdrs = response.headers.get('Content-Disposition');
        let file = 'document';
        if (hdrs != null) {
          const split = hdrs.split('filename=');
          if (split.length > 0) {
            file = split[1].split('"').join('');
          }
        }

        const u = window.URL.createObjectURL(response.body);
        const l = document.createElement('a');
        l.href = u;
        l.download = file;
        l.click();
      });
  }

  download(
    service: string,
    finish: (exit: boolean) => void,
    isFull = false,
    name = 'document.pdf',
    fileType = 'pdf',
  ) {
    let url = service;

    if (!isFull) {
      url = this.built(service);
    }

    const options = {
      observe: 'response' as 'body',
      responseType: 'blob' as 'json',
    };

    this.http.get<HttpResponse<any>>(url, options).subscribe({
      next: (response: any) => {
        if (response.body.type == 'application/json') {
          let reader = new FileReader();

          reader.addEventListener('loadend', (e: any) => {
            let json = JSON.parse(e.srcElement.result);
            alert(json.message.join('\n'));
          });

          reader.readAsText(response.body);

          finish(false);

          return;
        }

        let hdrs = response.headers.get('Content-Disposition');
        let file = 'document';
        if (hdrs != null) {
          let split = hdrs.split('filename=');

          if (split.length > 0) {
            file = split[1].split('"').join('');
          }
        }

        file += '.' + fileType;

        let u = window.URL.createObjectURL(response.body);
        let l = document.createElement('a');
        l.href = u;
        l.download = name?.includes('.') ? name : name + '.' + fileType;
        l.click();
        finish(true);
      },
      error: (error: any) => {
        console.error(error);

        let errorMsg = error.error.message
          ? error.error.message
          : error.message;
        alert(errorMsg);
        finish(false);
      },
    });
  }
}
